import React, { useEffect, useState } from "react";
import classNames from "classnames";
import _ from "lodash";
import * as Dialog from "@radix-ui/react-dialog";
import { Navigation as DsNavigation, NavigationItem, Text } from "@/ui";
import usePermissions from "../../hooks/auth/usePermissions";
import styles from "./Navigation.module.scss";

const ChevronDown = () => (
    <svg aria-hidden fill="none" height="18" viewBox="0 0 18 18" width="18">
        <path
            d="M4.5 6.75 9 11.25l4.5-4.5"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
        />
    </svg>
);

const CloseGlyph = () => (
    <svg aria-hidden fill="none" height="20" viewBox="0 0 20 20" width="20">
        <path
            d="M6 6l8 8M14 6l-8 8"
            stroke="currentColor"
            strokeLinecap="round"
            strokeWidth="1.8"
        />
    </svg>
);

const renderIcon = (icon, iconAsImage) => {
    if (!icon) {
        return undefined;
    }

    return (
        <span className={styles.icon}>
            {iconAsImage ? <img alt="" src={icon} /> : <i className={icon} />}
        </span>
    );
};

const Navigation = ({ items, activeItem, activeKey, setActiveKey }) => {
    const isAllow = usePermissions();

    const [active, setActive] = useState(() =>
        _.findIndex(items, i => i.render),
    );
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        if (!activeKey) return;

        const index = _.findIndex(items, i => i.key === activeKey);

        if (index !== -1) {
            action(index);
        }
    }, [activeKey, items]);

    const action = index => {
        window.scrollTo(0, 0);

        setActive(index);

        if (setActiveKey) {
            setActiveKey(items[index].key);
        }

        if (activeItem) {
            activeItem(items[index]);
        }
    };

    const selectSection = index => {
        action(index);
        setMenuOpen(false);
    };

    const activeSection = items[active];

    const renderNavList = onSelect => (
        <DsNavigation>
            <DsNavigation.List>
                {items.map((item, index) => {
                    if (!isAllow(item.permissions) || item.hidden) {
                        return null;
                    }

                    if (!item.label) {
                        return null;
                    }

                    if (!item.render) {
                        return (
                            <DsNavigation.ListItem
                                key={`section-${item.label}-${index}`}
                            >
                                <Text
                                    className={styles.sectionLabel}
                                    tone="secondary"
                                    variant="label-sm"
                                >
                                    {item.label}
                                </Text>
                            </DsNavigation.ListItem>
                        );
                    }

                    return (
                        <DsNavigation.ListItem
                            key={item.key || item.label || index}
                        >
                            <NavigationItem
                                active={index === active}
                                icon={renderIcon(item.icon, item.iconAsImage)}
                                nested={Boolean(item.invalid)}
                                onClick={() => onSelect(index)}
                                trailing={
                                    item.invalid ? (
                                        <i
                                            aria-label="Invalid section"
                                            className={classNames(
                                                "fas fa-exclamation",
                                                styles.invalidIcon,
                                            )}
                                        />
                                    ) : undefined
                                }
                                trailingStatus="warning"
                            >
                                {item.label}
                            </NavigationItem>
                        </DsNavigation.ListItem>
                    );
                })}
            </DsNavigation.List>
        </DsNavigation>
    );

    return (
        <div className={styles.root}>
            <div className={styles.mobilePicker}>
                <button
                    aria-expanded={menuOpen}
                    aria-haspopup="dialog"
                    className={styles.pickerTrigger}
                    onClick={() => setMenuOpen(true)}
                    type="button"
                >
                    <span className={styles.pickerLeading}>
                        {activeSection?.render
                            ? renderIcon(
                                  activeSection.icon,
                                  activeSection.iconAsImage,
                              )
                            : null}
                        <span className={styles.pickerLabel}>
                            {activeSection?.label || "Select section"}
                        </span>
                    </span>
                    <span aria-hidden className={styles.pickerChevron}>
                        <ChevronDown />
                    </span>
                </button>

                <Dialog.Root onOpenChange={setMenuOpen} open={menuOpen}>
                    <Dialog.Portal>
                        <Dialog.Overlay className={styles.sheetOverlay} />
                        <Dialog.Content
                            aria-describedby={undefined}
                            className={styles.sheet}
                        >
                            <div className={styles.sheetHeader}>
                                <Dialog.Title className={styles.sheetTitle}>
                                    Settings sections
                                </Dialog.Title>
                                <Dialog.Close asChild>
                                    <button
                                        aria-label="Close sections"
                                        className={styles.sheetClose}
                                        type="button"
                                    >
                                        <CloseGlyph />
                                    </button>
                                </Dialog.Close>
                            </div>
                            <div className={styles.sheetBody}>
                                {renderNavList(selectSection)}
                            </div>
                        </Dialog.Content>
                    </Dialog.Portal>
                </Dialog.Root>
            </div>

            <div className={styles.navColumn}>{renderNavList(action)}</div>

            <div className={styles.contentColumn}>
                {items.map((item, index) =>
                    item.render ? (
                        <div
                            className={classNames(styles.panel, {
                                [styles.panelHidden]: index !== active,
                            })}
                            key={item.key || item.label || index}
                        >
                            {item.render}
                        </div>
                    ) : null,
                )}
            </div>
        </div>
    );
};

export default Navigation;
